/**
 * Market Data Utilities
 *
 * Provides fictitious market average data for comparison purposes.
 * This data helps users understand how their financial situation compares
 * to others in their age group.
 *
 * Future: These values will be fetched using AI and stored in Firestore
 * when the user first signs in.
 */

import BigNumber from "bignumber.js";

/**
 * Age groups for market data segmentation
 */
export type AgeGroup = "18-29" | "30-39" | "40-49" | "50-59" | "60+";

/**
 * Market averages for a specific age group
 * All income and expense values are monthly
 */
export interface MarketAverages {
  ageGroup: AgeGroup;
  averageMonthlyIncome: number;
  averageMonthlyExpenses: number;
  averageTotalAssets: number;
  averageTotalLiabilities: number;
}

/**
 * Determine age group from current age
 */
export function getAgeGroup(age: number | null): AgeGroup {
  if (age === null || age < 18) return "18-29";
  if (age < 30) return "18-29";
  if (age < 40) return "30-39";
  if (age < 50) return "40-49";
  if (age < 60) return "50-59";
  return "60+";
}

/**
 * Fictitious market averages by age group (Canadian market, in CAD)
 * All income and expense values are monthly
 *
 * Note: Monthly income values are rounded from annual averages for simplicity.
 * Minor discrepancies exist when converting back to annual (e.g., 5167 * 12 = 62004 vs 62000).
 * This is acceptable as these are reference averages, not precise calculations.
 *
 * Data sources (for reference):
 * - Statistics Canada Average Income Data
 * - Canadian Financial Capability Survey
 * - Bank of Canada Consumer Debt Statistics
 *
 * Note: These are simplified averages for demonstration purposes.
 */
const MARKET_DATA: Record<AgeGroup, MarketAverages> = {
  "18-29": {
    ageGroup: "18-29",
    averageMonthlyIncome: 3500, // ~$42,000 annual
    averageMonthlyExpenses: 2800,
    averageTotalAssets: 18000,
    averageTotalLiabilities: 15000,
  },
  "30-39": {
    ageGroup: "30-39",
    averageMonthlyIncome: 5167, // ~$62,000 annual
    averageMonthlyExpenses: 3800,
    averageTotalAssets: 85000,
    averageTotalLiabilities: 48000,
  },
  "40-49": {
    ageGroup: "40-49",
    averageMonthlyIncome: 6250, // $75,000 annual
    averageMonthlyExpenses: 4500,
    averageTotalAssets: 185000,
    averageTotalLiabilities: 62000,
  },
  "50-59": {
    ageGroup: "50-59",
    averageMonthlyIncome: 6500, // $78,000 annual
    averageMonthlyExpenses: 4200,
    averageTotalAssets: 320000,
    averageTotalLiabilities: 45000,
  },
  "60+": {
    ageGroup: "60+",
    averageMonthlyIncome: 4333, // ~$52,000 annual
    averageMonthlyExpenses: 3500,
    averageTotalAssets: 450000,
    averageTotalLiabilities: 18000,
  },
};

/**
 * Get market averages for a specific age
 *
 * @param age - User's current age (or null if not available)
 * @returns Market averages for the user's age group
 */
export function getMarketAverages(age: number | null): MarketAverages {
  const ageGroup = getAgeGroup(age);
  return MARKET_DATA[ageGroup];
}

/**
 * Format a market average value with comparison indicator
 *
 * @param userValue - User's value
 * @param marketValue - Market average value
 * @returns Comparison string (e.g., "23% above average", "15% below average", "at average")
 */
export function formatComparison(
  userValue: number,
  marketValue: number
): string {
  if (marketValue === 0) {
    return "N/A";
  }

  const difference = new BigNumber(userValue).minus(marketValue).toNumber();
  const percentDiff = Math.abs(
    new BigNumber(difference).dividedBy(marketValue).times(100).toNumber()
  );

  if (percentDiff < 5) {
    return "at average";
  }

  const rounded = Math.round(percentDiff);

  if (difference > 0) {
    return `${rounded}% above average`;
  } else {
    return `${rounded}% below average`;
  }
}

/**
 * Calculate percentile ranking based on comparison to peer group average
 *
 * Estimates where the user ranks relative to their peer group using a simplified model:
 * - Assumes normal distribution around the average
 * - For assets/income: higher values = higher percentile (better)
 * - For liabilities/expenses: lower values = higher percentile (better)
 *
 * @param userValue - User's value
 * @param marketValue - Market average value for peer group
 * @param higherIsBetter - True for assets/income, false for liabilities/expenses
 * @returns Percentile (e.g., 75 means "Top 25%" since user is in 75th percentile)
 */
export function calculatePercentile(
  userValue: number,
  marketValue: number,
  higherIsBetter: boolean = true
): number {
  if (marketValue === 0) {
    return 50; // Default to median if no market data
  }

  // Calculate how far the user is from average (in standard deviations)
  // Using a simplified model where 1 std dev = 30% of mean
  const stdDev = new BigNumber(marketValue).times(0.3).toNumber();
  const difference = new BigNumber(userValue).minus(marketValue).toNumber();
  const zScore = new BigNumber(difference).dividedBy(stdDev).toNumber();

  // Convert z-score to percentile using simplified normal distribution
  // Cap z-score between -3 and 3 (99.7% of normal distribution)
  const cappedZ = Math.max(-3, Math.min(3, zScore));

  // Approximate percentile from z-score
  // Using a simple mapping: z=-3 → 0.1%, z=0 → 50%, z=3 → 99.9%
  let percentile = 50 + (cappedZ / 3) * 49;

  // For liabilities/expenses, invert the percentile (lower is better)
  if (!higherIsBetter) {
    percentile = 100 - percentile;
  }

  // Ensure percentile is between 1 and 99
  return Math.max(1, Math.min(99, Math.round(percentile)));
}

/**
 * Format percentile as "Top X%" string
 *
 * @param percentile - Percentile value (1-99)
 * @returns Formatted string (e.g., "Top 25%" for 75th percentile)
 */
export function formatPercentile(percentile: number): string {
  const topPercent = 100 - percentile;
  return `Top ${topPercent}%`;
}
